/**
 * `/time-tracking` — one of the two screens spec 01 §3 gives an employee.
 *
 * The shape file is `web/shapes/time-tracking.md` and it was committed first. The
 * hierarchy here is its §2: the timer, then the search that feeds it, then the
 * entries, then manual entry last and collapsed — it is the one door through which
 * a number enters the service on a human's say-so, and it should not look like the
 * default way to record time.
 *
 * The screen owns **no timer state**. `TimerPanel` and the top bar's `MiniTimer` are
 * two readers of the one `TimerProvider` mounted above the router; this file does
 * not even import the entry type. See `web/src/timer/TimerProvider.tsx`.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { addDays, localDateIn, startOfLocalDay } from '../i18n/datetime';
import { useAuthenticatedUser } from '../auth/SessionProvider';
import { LastSync } from '../states/LastSync';
import { ScreenHeading } from '../shell/ScreenHeading';
import { EntryList } from './timetracking/EntryList';
import { ManualEntryForm } from './timetracking/ManualEntryForm';
import { TaskSearch } from './timetracking/TaskSearch';
import { TimerPanel } from './timetracking/TimerPanel';

/** How far back the entries list looks by default. A working fortnight. */
const RANGE_DAYS = 14;

export function TimeTrackingScreen(): ReactNode {
  const { t } = useI18n();
  const user = useAuthenticatedUser();
  const timezone = user.timezone;

  // Fixed at mount rather than recomputed on every render: a range whose end moves
  // with the clock changes the request path on each tick of the timer's interval,
  // and `useApiQuery` keys on the path.
  const [today] = useState(() => localDateIn(timezone, Date.now()));
  const fromIso = startOfLocalDay(timezone, addDays(today, -RANGE_DAYS));
  const toIso = startOfLocalDay(timezone, addDays(today, 1));

  return (
    <div className="wp-stack wp-b">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.timeTracking')}</ScreenHeading>
        {/* `agentSyncAt` is null here and rendered as "chưa từng" rather than hidden:
            this screen reads no device heartbeat, and inventing one from a time entry
            would be a claim about a machine made from a person's typing. The achau
            side is real — `TaskSearch` reports the cache's own age beside the rows
            it qualifies. */}
        <LastSync agentSyncAt={null} achauSyncAt={null} timezone={timezone} />
      </div>

      <TimerPanel timezone={timezone} />
      <TaskSearch timezone={timezone} />
      <EntryList fromIso={fromIso} toIso={toIso} timezone={timezone} rangeDays={RANGE_DAYS} />
      <ManualEntryForm timezone={timezone} />
    </div>
  );
}
